import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ShieldPlus, ImageIcon } from 'lucide-react';

interface CreateBadgeFormProps {
  onSuccess?: () => void;
}

export function CreateBadgeForm({ onSuccess }: CreateBadgeFormProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [d, setD] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [thumb, setThumb] = useState('');

  const slugify = (val: string) =>
    val
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const handleNameChange = (val: string) => {
    setName(val);
    if (!d || d === slugify(name)) {
      setD(slugify(val));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const dTag = d.trim();
    if (!dTag) {
      toast({ title: 'Badge ID is required', variant: 'destructive' });
      return;
    }

    const tags: string[][] = [['d', dTag]];
    if (name.trim()) tags.push(['name', name.trim()]);
    if (description.trim()) tags.push(['description', description.trim()]);
    if (image.trim()) tags.push(['image', image.trim(), '1024x1024']);
    if (thumb.trim()) tags.push(['thumb', thumb.trim(), '256x256']);

    try {
      await publish({ kind: 30009, content: '', tags });

      toast({
        title: 'Badge created!',
        description: `"${name || dTag}" has been published to Nostr.`,
      });

      // Reset
      setD('');
      setName('');
      setDescription('');
      setImage('');
      setThumb('');

      await queryClient.invalidateQueries({ queryKey: ['nostr', 'badge-definitions', user.pubkey] });
      onSuccess?.();
    } catch (err) {
      toast({
        title: 'Failed to create badge',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 text-center">
          <p className="text-muted-foreground">Please log in to create badges.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/60 bg-card/80 backdrop-blur">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <ShieldPlus className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Create New Badge</CardTitle>
        </div>
        <CardDescription>
          Define a new NIP-58 badge (kind 30009). Once created, you can award it to any npub.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="badge-name">Badge Name</Label>
              <Input
                id="badge-name"
                placeholder="e.g. Medal of Bravery"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="badge-id">
                Badge ID{' '}
                <span className="text-xs text-muted-foreground">(slug, immutable)</span>
              </Label>
              <Input
                id="badge-id"
                placeholder="e.g. medal-of-bravery"
                value={d}
                onChange={(e) => setD(slugify(e.target.value))}
                required
                pattern="[a-z0-9\-]+"
                title="Lowercase letters, numbers, and hyphens only"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="badge-desc">Description</Label>
            <Textarea
              id="badge-desc"
              placeholder="Describe the badge, what it represents, or how it's earned…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="badge-image" className="flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5" /> Image URL
                <span className="text-xs text-muted-foreground">(1024×1024 recommended)</span>
              </Label>
              <Input
                id="badge-image"
                type="url"
                placeholder="https://example.com/badge.png"
                value={image}
                onChange={(e) => setImage(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="badge-thumb" className="flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5" /> Thumbnail URL
                <span className="text-xs text-muted-foreground">(256×256)</span>
              </Label>
              <Input
                id="badge-thumb"
                type="url"
                placeholder="https://example.com/badge_256.png"
                value={thumb}
                onChange={(e) => setThumb(e.target.value)}
              />
            </div>
          </div>

          {image && (
            <div className="flex items-center gap-3 rounded-lg border border-border/40 bg-muted/30 p-3">
              <img
                src={image}
                alt="Badge preview"
                className="h-16 w-16 rounded-lg object-cover border border-border/40"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div>
                <p className="text-sm font-medium">{name || 'Unnamed Badge'}</p>
                <p className="text-xs text-muted-foreground">{d || 'no-id-yet'}</p>
              </div>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? 'Publishing…' : 'Create Badge'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
